import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import AppLayout from '../../component/appLayout';
import NavbarMessageData from '../../component/navbarMessageData';
import { getChatGroupList, setLastReadForDiscussion } from '../../services/chatMessageSyncService';
import { ChatGroupInterface } from '../../interfaces/chatMessageInterface';
import { getUrlParam, isEmpty } from '../../helpers/common';
import { useHistory } from 'react-router-dom';
import { captureException } from '../../services/logService';
import MessageGroupCacheService from '../../services/messageGroupCacheService';
import { useMobileDevice } from '../../global/useMobile';
import { preloadOpenChatMemory, refreshOpenChatGroupMessages } from '../../services/conversationSyncService';
import { rootStore } from '../../mobx/rootStore';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';

const Message = observer(() => {
  // States
  const [loading, setLoading] = useState(false);

  //mobx variables
  const { workspaceStore, chatMessageStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { chatGroups, currentChatGroup } = chatMessageStore;

  // Other variables
  const history = useHistory();
  const mobile = useMobileDevice();

  // update current group
  const changeCurrentGroup = useCallback(
    async (sortedData: ChatGroupInterface[]) => {
      const group = getUrlParam(history.location.search, 'chatGroup');
      if (sortedData?.length > 0 && isEmpty(group)) {
        const lastOpenGroupId = UserPreferenceSingleton.getInstance().getLastMessageGroup();
        const lastOpenGroup = sortedData?.find((x) => x?.id === lastOpenGroupId);
        if (lastOpenGroup) {
          chatMessageStore.setCurrentChatGroup(lastOpenGroup);
        } else {
          chatMessageStore.setCurrentChatGroup(sortedData[0]);
        }
      } else if (sortedData?.length > 0) {
        const currentGroup = sortedData?.find((x) => x?.id === group) || null;
        chatMessageStore.setCurrentChatGroup(currentGroup);
      }
    },
    [chatMessageStore, history.location.search]
  );

  // Filter pinned groups
  const filterPinnedGroups = useCallback((groups: ChatGroupInterface[]) => {
    return groups
      .filter((group) => group?.order !== undefined && group?.order !== null) // Filter pinned groups
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  }, []);

  // Filter unpinned groups
  const filterUnpinnedGroups = useCallback(
    (groups: ChatGroupInterface[]) => groups.filter((group) => group?.order === undefined || group?.order === null),
    []
  );

  // load initial data
  const loadData = useCallback(
    async (isSetInitially?: boolean) => {
      try {
        if (currentWorkspace?.id) {
          setLoading(true);
          if (isSetInitially) {
            preloadOpenChatMemory();
            // Show the last known conversation list instantly while the fresh one loads.
            const cachedGroups = await MessageGroupCacheService.getInstance().getMessageGroups();
            if (cachedGroups?.length > 0) {
              chatMessageStore.setChatGroups(cachedGroups);
              chatMessageStore.setPinChatGroups(filterPinnedGroups(cachedGroups));
              chatMessageStore.setUnpinChatGroups(filterUnpinnedGroups(cachedGroups));
            }
          }
          const previousGroupId = chatMessageStore.currentChatGroup?.id;
          const groups = await getChatGroupList(!!isSetInitially);
          if (groups) {
            chatMessageStore.setPinChatGroups(filterPinnedGroups(groups));
            chatMessageStore.setUnpinChatGroups(filterUnpinnedGroups(groups));
            await changeCurrentGroup(groups);
            // A newly opened group is loaded by MessageDetail; refresh one that stayed open.
            const current = chatMessageStore.currentChatGroup;
            if (current?.id && current.id === previousGroupId) {
              await refreshOpenChatGroupMessages(current.id, current.type, true);
            }
          }
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [currentWorkspace?.id, chatMessageStore, filterPinnedGroups, filterUnpinnedGroups, changeCurrentGroup]
  );

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (chatGroups && chatGroups.length > 0) {
      const groupIdFromUrl = getUrlParam(history.location.search, 'chatGroup');
      if (groupIdFromUrl) {
        const groupFromUrl = chatGroups.find((group) => group.id === groupIdFromUrl);

        if (groupFromUrl && groupFromUrl.id !== currentChatGroup?.id) {
          chatMessageStore.setCurrentChatGroup(groupFromUrl);
        }
      } else {
        changeCurrentGroup(chatGroups);
      }
    }
  }, [history.location.search, chatMessageStore, changeCurrentGroup, chatGroups, currentChatGroup?.id]);

  // React to background message updates. The single App-level Firebase
  // 'messages' listener refreshes chatGroups in the store; recompute the
  // derived pinned/unpinned lists and the open conversation from it instead of
  // running a duplicate listener here.
  useEffect(() => {
    chatMessageStore.setPinChatGroups(filterPinnedGroups(chatGroups));
    chatMessageStore.setUnpinChatGroups(filterUnpinnedGroups(chatGroups));
    if (currentChatGroup?.id) {
      const updatedCurrentGroup = chatGroups.find((group) => group?.id === currentChatGroup.id);
      if (updatedCurrentGroup) {
        chatMessageStore.setCurrentChatGroup(updatedCurrentGroup);
      }
    }
  }, [chatGroups, currentChatGroup?.id, chatMessageStore, filterPinnedGroups, filterUnpinnedGroups]);

  const onLeaveMessages = useCallback(
    async (isCallFromReturn: boolean) => {
      if (isCallFromReturn && !mobile) {
        await setLastReadForDiscussion();
        chatMessageStore.setCurrentChatGroup(null);
      }
    },
    [chatMessageStore, mobile]
  );

  useEffect(() => {
    return () => {
      onLeaveMessages(true);
    };
  }, [onLeaveMessages]);

  return (
    <AppLayout isMessageBox={true}>
      <NavbarMessageData loadData={() => loadData(false)} loading={loading} />
    </AppLayout>
  );
});

export default Message;
